import { Row, Card } from 'antd'

import Pregnant from '../../styles/HalfPregnant.module.css'

const HalfPregnant = () => {
  return (
    <div className={`${Pregnant["half-pregnant-screen"]}`}>
      <Row justify="center">
        <Card className={`${Pregnant["half-pregnant-card"]}`}>
          <article>
            <span>AUGUST 2, 2021 &nbsp;|&nbsp;NJLJ.COM</span>

            <h1 className={`${Pregnant["half-pregnant-title"]}`}>The Half-Pregnant Public Policy on Car Insurance Rates</h1>
            <br />

            <p className={`${Pregnant["half-pregnant-author-name"]}`}>By Eric S. Poe</p>
            <hr />
            <figure className={`${Pregnant["right"]}`}>
              <img alt="Eric Poe - CURE Ceo" src="https://assets.cure.com/img/main-site/eric_half_pregnant.jpeg" />
              <figcaption>Eric S. Poe, Esq., CPA</figcaption>
            </figure>
            <p>
              In my recent Law Journal article{' '}
              <a
                className={`${Pregnant["half-pregnant-hyperlinks"]}`}
                href="https://www.law.com/njlawjournal/2021/07/09/a-dirty-little-secret-in-car-insurance-can-lawyers-lead-a-change/"
                rel="noreferrer"
                target="_blank"
              >
                &quot;Dirty Little Secret in Car Insurance: Can lawyers Lead a Change?&quot;
              </a>{' '}
              I explained the little-known practice of using income proxies in car insurance in New Jersey.
              Specifically, it brought to light the dirty secret that many of the publicly traded insurance companies,
              including those with very familiar household names, charge significantly higher rates to drivers with
              spotless driving records simply because they have lower paying jobs, are unemployed, or don&apos;t have at
              least a college degree, a credit score above 700, or own a home. Every day, the car insurance industry is
              wrongly considering these factors when determining the eligibility of, or calculating insurance rates for,
              a particular person. It should come as no surprise that this practice disproportionately harms low income
              and minority communities, essentially holding people back from economic opportunities simply because they
              are more likely to be poor.
            </p>

            <p>
              Despite numerous consumer watch group studies and their criticism of this practice, drivers are not being
              notified that they are being charged higher rates or being sent to less attractive insurance companies on
              the basis of their socioeconomic status. The result—voters and consumers are not complaining. But a lack
              of formal and filed complaints does not mean that it is not happening and that the practice shouldn&apos;t
              be banned. The time for action is now.
            </p>

            <h5>A Regulation With No Teeth</h5>
            <p>
              By way of background, the Legislature for generations has explicitly banned rates, or the use of factors,
              that are &quot;unfairly discriminatory&quot;. See N.J.S.A. 17:29A-7 (&quot;Rating system, approval of;
              revision or modification&quot;). Yet what is considered to be &quot;unfairly discriminatory?&quot; It
              seems to be subjective in nature, and I would argue it is why these obvious income proxies are being
              permitted. If the regulators believe a factor has a proven correlation to profitability (loss ratio), they
              could approve those factors for use because it can be interpreted that using such a factor is therefore
              &quot;fair.&quot;
            </p>

            <p>
              Oddly, back in 2008, the regulators at the New Jersey Department of Banking and Insurance (DOBI)
              identified factors that alone could not be used to reject a driver, explicitly stating in the regulations
              that it prohibits the use of &quot;occupation, education or insurance score of the applicant&quot; when
              insurance carriers are deciding whether to accept a driver. See N.J.A.C. 11:3-8.12.
            </p>

            <figure className={`${Pregnant["left"]}`}>
              <img
                alt="car-insurance"
                className={`${Pregnant["half-pregnant-screen-left-img"]}`}
                src="https://assets.cure.com/img/main-site/blog2.png"
              />
            </figure>

            <p>
              It appears that at least some carriers have not followed the letter or spirit of that regulation. The New
              Jersey GEICO Automobile Guide to Company Placement, publicly available, indicates that as recently as
              2010, GEICO did in fact consider education and occupation when deciding whether to accept applicants and
              how to place them into the various tiers of GEICO companies that operate in New Jersey. Specifically, this
              document explains &quot;Education - Risks who have achieved at least a high school diploma or its
              equivalent are more favorable than those without a high school education. Bachelors, Masters, and other
              advanced degrees are considered most favorable.&quot; This &quot;guide&quot; goes on to provide sample
              education levels and occupations for eight different &quot;group definitions.&quot; This troubling
              consideration of these factors clearly raises the question as to whether, or better yet, why this is still
              happening today.
            </p>
            <p>
              Equally disconcerting is, if our regulators in 2008 recognized that these income-related factors have no
              &quot;fair&quot; place in at least part of the insurance application and rejection process, then why allow
              them at all? It seems like a distinction without a difference. I analogize it to saying a person cannot be
              rejected entrance into a restaurant based upon their race, but once they sit down and order food, they can
              be charged twice as much as the preferred race. Why are we being &quot;half-pregnant&quot; in our public
              policy when it comes to how these factors should play a role in car insurance rates or underwriting?
            </p>

            <h5>Community Impact of This Secret Practice</h5>

            <p>
              This is not a victimless problem. Charging secret and unaffordable rates to drivers simply because they
              are less educated, hold lesser paying jobs and have lower credit scores, regardless of whether they are a
              safe driver, prevents a family the fair opportunity to maintain a job. It strips a person of the ability
              to afford a car legally, relegating those individuals to bike routes and bus stops.
            </p>

            <p>
              Sadly, this practice of discriminating against the poor is a widely known issue to public policy makers,
              yet we still do not see reform. According to a 2016 analysis by the Consumer Federation of America, major
              car insurers use income proxies as a basis for charging higher rates to safe drivers simply because they
              do not have a college degree or high paying job
              <a
                className={`${Pregnant["half-pregnant-hyperlinks"]}`}
                href="https://consumerfed.org/cfa-studies-on-the-plight-of-low-and-moderate-income-good-drivers-in-affording-state-required-auto-insurance/"
                rel="noreferrer"
                target="_blank"
              >
                (https://consumerfed.org/cfa-studies-on-the-plight-of-low-and-moderate-income-good-drivers-in-affording-state-required-auto-insurance/(&quot;CFA
                Reports&quot;)).
              </a>
            </p>

            <p>
              Furthermore, in January 2021, Consumer Reports did a comprehensive study to understand the nature of auto
              insurance companies pricing practices. It specifically reviewed the use of education level and occupation,
              collecting hundreds of price quotes for one hypothetical driver, with the only variation made on education
              level and job category. For the study, Consumer Reports collected 869 unique online policy price quotes
              from nine auto insurers, using 21 ZIP codes in six states. The same driver profile was used for all
              quotes, with the only variation made to education level and job category. The research concluded that less
              formally educated applicants, or those in lower paying jobs, were quoted higher prices, regardless of
              driving record{' '}
              <a
                className={`${Pregnant["half-pregnant-hyperlinks"]}`}
                href="https://advocacy.consumerreports.org/wp-content/uploads/2021/01/Auto-Insurance-White-Paper-Report-FINAL1.26C.pdf"
                rel="noreferrer"
                target="_blank"
              >
                https://advocacy.consumerreports.org/wp-content/uploads/2021/01/Auto-Insurance-White-Paper-Report-FINAL1.26C.pdf
              </a>{' '}
              (&quot;Report&quot;). See, also, Kaveh Waddell,{' '}
              <a
                className={`${Pregnant["half-pregnant-hyperlinks"]}`}
                href="https://www.consumerreports.org/car-insurance/why-your-education-and-job-could-mean-youre-paying-too-much-for-car-insurance/"
                rel="noreferrer"
                target="_blank"
              >
                &quot;Why Your Education and Job Could Mean You&apos;re Paying Too Much for Car Insurance,&quot;
              </a>{' '}
              Consumer Reports, Jan. 28, 2021.
            </p>
            <p>
              This Consumer Reports study highlights the disparate impact these practices have on families of color,
              particularly Black and Hispanic communities, noting that in 2019, 40% of non-Hispanic Whites 25 years and
              older held a bachelor&apos;s degree or higher, compared to 26% and almost 19% of Black and Latinx
              Americans, respectively. It doesn&apos;t take a rocket scientist to know that lower educated people make
              less money in this country. Simply pick up a U.S. Census for the last 50 years to draw that conclusion.
            </p>
            <p>
              This is made even more egregious when you consider that those living in urban areas and those with denser
              populations are already subject to higher premiums. It should come as no surprise that Black and Hispanic
              families are more likely than White families to live in these communities, and therefore start out behind
              the proverbial eight ball of higher rates.
            </p>

            <h5>New Jersey Must Do Better</h5>

            <p>
              Considering this is where I first discovered the practice of using education and occupation for car
              insurance rating in 2004, New Jersey should (and can) be a leader in preventing this discriminatory
              practice. Recently several states, including New York, Michigan and Washington, have curtailed the use of
              income proxies when establishing car insurance rates. There is also movement at the national level to stop
              this practice. In 2019, U.S. Representative and former New Jersey State Assemblywoman Bonnie Watson
              Coleman (D-NJ), alongside Representative Rashida Tlaib (D-MI), courageously stood up to corporate America
              and introduced the Prohibit Auto Insurance Discrimination Act (PAID Act), which would eliminate the use of
              such factors in car insurance rates. In September 2020, our own Senator Cory Booker (D-NJ) introduced the
              same legislation in the Senate.
            </p>

            <p>
              It is time for New Jersey legislators to act. The Fair Auto Insurance (FAIR) Act A-1657/S-111 has already
              passed the New Jersey Senate and now awaits action by the New Jersey Assembly Committee on Financial
              Institutions and Insurance. If enacted into law, this legislation would dismantle one critical building
              block in the framework of inequality in New Jersey. All residents deserve fairness in the price they pay
              for coverage — a rate based on an individual&apos;s driving history and safety, and not on where a person
              works or whether he or she has a college degree. A ban on the use of these income proxies will not only
              create fairness in the cost of car insurance but will right a wrong that is long overdue.
            </p>

            <hr />

            <p className={`${Pregnant["author"]}`}>
              <i>
                Eric S. Poe, attorney and CPA, is principal and serves as chief executive officer and complex claims
                litigation officer for Citizens United Reciprocal Exchange (CURE) auto insurance, and New Jersey
                Physicians United Reciprocal Exchange (NJ PURE), a leading medical malpractice insurance carrier in the
                state.
              </i>
            </p>

            <hr />
            <p className={`${Pregnant["half-pregnant-foot-note"]}`}>
              Reprinted with permission from the August 2, 2021 edition of the NEW JERSEY LAW JOURNAL.© 2021 ALM Media
              Properties, LLC. All rights reserved. Further duplication without permission is prohibited. For
              information, contact 877-256-2472, reprints@alm.com or visit{' '}
              <a className={`${Pregnant["half-pregnant-hyperlinks"]}`} href="https://almreprints.com/" rel="noreferrer" target="_blank">
                www.almreprints.com
              </a>
              . NJLJ-07302021-499597
            </p>
            <hr />
          </article>
        </Card>
      </Row>
    </div>
  )
}

export default HalfPregnant
